import { useConfigParams } from "@/hooks/use-config-params"
import { isRecaptchaError } from "@/hooks/use-recaptcha"
import { TAuthType } from "@/lib/auth-type"
import { getAuthenticatedLinkIfFromReservedArea } from "@/lib/callback-url"
import useGlobalParamsStore from "@/store/global-params-store"
import { Err, isOk, Ok, Result } from "@pacote/result"
import Cookies from "js-cookie"
import useSWRMutation from "swr/mutation"
import { v4 as uuidV4 } from "uuid"
import { z } from "zod"
import { AppChannel, Channel } from "../config/channel"
import {
  ApiFailure,
  baseApiErrorResponseSchema,
  baseApiResponseSchema,
  UnhandledApiError,
} from "./api-config"
import { baseClientFetcher } from "./base-fetchers"
import { FetcherErrorResponse } from "./create-fetcher"
import { InfoMessageList, messagesFetcher } from "./get-messages"
import { useLoginData } from "./hooks/use-login-data"
import { TSsoLoginResponse } from "./post-login-sso"

/* -------------------------------------------------------------------------------------------------
 * types
 * -----------------------------------------------------------------------------------------------*/

type BaseLoginPayload = {
  password: string
  username: string
  channel: Channel
  recaptchaToken: string
}

type LoginPayload = BaseLoginPayload & {
  pvd?: string
}

const contactsToVerifySchema = z.object({
  verificationEmailState: z.number().optional(),
  verificationPhoneNumberState: z.number().optional(),
  contacts: z
    .object({
      name: z.string().optional(),
      surname: z.string().optional(),
      prefix: z.string().optional(),
      phoneNumber: z.string().optional(),
      email: z.string().optional(),
      phoneLockDays: z.number().optional(),
      emailLockDays: z.number().optional(),
    })
    .optional(),
})

const loginResponseSchema = baseApiResponseSchema.extend({
  token: z.string().optional(),
  tokenTouch: z.string().optional(),
  billCodeId: z.number().optional(),
  passwordExpirationDays: z.number(),
  passwordRequired: z.boolean().optional(),
  isLoginBlocked: z.boolean(),
  isSecurityQuestionRequired: z.boolean(),
  isBillNotProtocolled: z.boolean(),
  flagAdvPlus: z.boolean().optional(),
  loginBlockedType: z.string().optional(),
  loginBlockedMessage: z.string().optional(),
  emailAddress: z.string().optional(),
  prefix: z.string().optional(),
  phoneNumber: z.string().optional(),
  fiscalCode: z.string().optional(),
  contactsToVerify: contactsToVerifySchema.optional(),
  voucherStatus: z.string().optional(),
  remainingVoucherDays: z.number().optional(),
  contractSettings: z.number().optional(), // 0 means contract not yet accepted, anything else means contract accepted
})

type LoginResponse = z.infer<typeof loginResponseSchema>

type ComputedLoginFields = {
  postLoginAction: MultiAction
  hasOther: boolean
  messages: InfoMessageList
}

type TloginResponse = LoginResponse & ComputedLoginFields

type MultiAction = {
  type:
    | "ACCOUNT_BLOCKED"
    | "QUESTIONARIO"
    | "VERIFY_CONTACTS"
    | "PASSWORD_EXPIRING"
    | "URL_REDIRECT"
    | "NOTHING"
  showListModals?: boolean
}

type ContactsToVerify = z.infer<typeof contactsToVerifySchema>

const loginErrorResponseSchema = baseApiResponseSchema
  .merge(baseApiErrorResponseSchema)
  .extend({
    passwordExpirationDays: z.number().optional(),
    isLoginBlocked: z.boolean().optional(),
    isSecurityQuestionRequired: z.boolean().optional(),
    isBillNotProtocolled: z.boolean().optional(),
    flagAdvPlus: z.boolean().optional(),
  })

type LoginErrorResponse = z.infer<typeof loginErrorResponseSchema>

type TloginErrorResponse = {
  type: "SOGEI" | "UNKNOWN" | "CREDENTIALS" | "ERROR_RECAPTCHA"
  counter: number
  errorResponse: LoginErrorResponse
}

type LoginResult = Result<TloginResponse, TloginErrorResponse>
type OkLoginResponse = Ok<TloginResponse>

/* -------------------------------------------------------------------------------------------------
 * config
 * -----------------------------------------------------------------------------------------------*/

const loginServiceEndpoint = "/authentication-ms/v1/login"

/* -------------------------------------------------------------------------------------------------
 * utils
 * -----------------------------------------------------------------------------------------------*/

function getRedirectUrl(
  response: TloginResponse | TSsoLoginResponse,
  endcallbackurl: string,
  flagConto: number,
  authType: TAuthType,
  channel: Channel,
) {
  if (!response || response.isLoginBlocked) return ""

  if (
    authType === "oauth" &&
    "redirect" in response &&
    typeof response.redirect === "string"
  ) {
    return response.redirect
  }

  const token =
    "jwt" in response && response.jwt != null
      ? response.jwt
      : "token" in response && response.token != null
        ? response.token
        : ""

  switch (channel) {
    case AppChannel.PortaleWebFullResponsiveWwwSisalIt:
      return (
        getAuthenticatedLinkIfFromReservedArea(
          endcallbackurl,
          token,
          authType,
        ) ?? window.APP_MAIN_SITE_HOMEPAGE
      )
    case AppChannel.SalvaIlBottino:
      let url = `${endcallbackurl}/?auth-token=${token}`

      const AK_AALB_COOKIE_NAME = window.LOGIN_AK_AALB_COOKIE_NAME

      const akaalbAreaPrivataK8sCookie = Cookies.get(AK_AALB_COOKIE_NAME)
      if (akaalbAreaPrivataK8sCookie != null) {
        const s = akaalbAreaPrivataK8sCookie.split("farm-")[1]
        url += `&f=${s != null ? s.charAt(0) : ""}`
      }
      return url
    default:
      return `http://appmobile.sisal.it/?auth-token=${token}&jwtToken=${response.tokenTouch}&flagConto=${flagConto}`
  }
}

async function seapServiceControl(billCodeId: number) {
  if (!window.seapweb) {
    console.error("seapweb not available")
    return
  }

  try {
    await window.seapweb.setUserAuthenticated(true)
    await window.seapweb.setUserId(billCodeId.toString())
    await window.seapweb.setExternalId(uuidV4())
    await window.seapweb.closeToken()
  } catch (error) {
    console.error("Error in seapServiceControl", error)
  }
}

function isPasswordExpiring(passwordExpirationDays: number) {
  return 0 < passwordExpirationDays && passwordExpirationDays < 8
}

function isSogeiError(error: string) {
  return error === "err.e8001" || error === "err.e8012"
}

function computePostLoginAction<T extends LoginResponse = LoginResponse>(
  response: T,
  channel: number,
  messages: InfoMessageList,
): MultiAction {
  const {
    isLoginBlocked,
    isSecurityQuestionRequired,
    passwordExpirationDays,
    flagAdvPlus,
    contactsToVerify,
  } = response

  if (isLoginBlocked) return { type: "ACCOUNT_BLOCKED" }
  if (flagAdvPlus) return { type: "QUESTIONARIO" }

  if (
    contactsToVerify &&
    (contactsToVerify.verificationEmailState !== 0 ||
      contactsToVerify.verificationPhoneNumberState !== 0)
  ) {
    return { type: "VERIFY_CONTACTS" }
  }

  const channelsWithPasswordExpiringFlow: Array<number> = [
    AppChannel.PortaleWebFullResponsiveWwwSisalIt,
    AppChannel.ClientDesktopSisalPoker,
  ]

  const expiringPassword = isPasswordExpiring(passwordExpirationDays)

  if (
    !isSecurityQuestionRequired &&
    messages.length === 0 &&
    (!channelsWithPasswordExpiringFlow.includes(channel) || !expiringPassword)
  ) {
    return { type: "URL_REDIRECT" }
  }

  return {
    type:
      channelsWithPasswordExpiringFlow.includes(channel) && expiringPassword
        ? "PASSWORD_EXPIRING"
        : "NOTHING",
    showListModals: messages.length > 0,
  }
}

function computeHasOther<T extends LoginResponse = LoginResponse>(
  loginResponse: T,
  messages: InfoMessageList,
) {
  const { passwordExpirationDays, isSecurityQuestionRequired } = loginResponse
  const hasOther =
    isPasswordExpiring(passwordExpirationDays) ||
    isSecurityQuestionRequired ||
    messages.length > 0
  return hasOther
}

function computeErrorType(response: FetcherErrorResponse<LoginErrorResponse>) {
  if (response.status !== 422) return "UNKNOWN"

  const sogeiError =
    response.data.error?.errorInfos?.generic?.some(isSogeiError) ?? false
  if (sogeiError) return "SOGEI"

  const message = response.data.error?.message

  if (isRecaptchaError(response.data)) return "ERROR_RECAPTCHA"

  if (message !== "") return "CREDENTIALS"

  return "UNKNOWN"
}

/* -------------------------------------------------------------------------------------------------
 * fetcher
 * -----------------------------------------------------------------------------------------------*/

const loginFetcher = async (
  key: string,
  { arg }: { arg: LoginPayload },
): Promise<LoginResult> => {
  const { password, username, pvd, channel, recaptchaToken } = arg
  /*--------------------acquire recaptcha token--------------------*/

  const sessionId = uuidV4()
  /*--------------------make login request--------------------*/

  const loginServiceResult = await baseClientFetcher<
    LoginResponse,
    LoginErrorResponse
  >(
    {
      url: key,
      method: "POST",
      headers: {
        "X-Session-Id": sessionId,
        "x-set-queue": "true",
      },
      body: {
        username,
        password,
        pvd: pvd || "BO9999",
        flagConto: 0,
        sessionId: sessionId,
        recaptchaToken,
        userAgent: navigator.userAgent,
      },
      successSchemaValidator: loginResponseSchema,
      errorSchemaValidator: loginErrorResponseSchema,
      throwOnValidationError: true,
    },
    { enrichRequestData: false },
  )

  /*--------------------handle success response--------------------*/
  if (isOk(loginServiceResult)) {
    sessionStorage.setItem("session-id", sessionId)

    const { token, status, billCodeId } = loginServiceResult.value

    let messages: InfoMessageList = []

    if (token) {
      useGlobalParamsStore.getState().setToken(token)

      const messagesServiceResult = await messagesFetcher()

      if (isOk(messagesServiceResult)) {
        messages = messagesServiceResult.value.infoMessageList
      }
    }

    if (
      status === "SUCCESS" &&
      channel === AppChannel.PortaleWebFullResponsiveWwwSisalIt &&
      billCodeId != null
    ) {
      await seapServiceControl(billCodeId)
    }

    const postLoginAction = computePostLoginAction(
      loginServiceResult.value,
      channel,
      messages,
    )

    const hasOther = computeHasOther(loginServiceResult.value, messages)

    return Ok({
      ...loginServiceResult.value,
      hasOther,
      messages,
      postLoginAction,
    })
  }
  /*--------------------handle error response--------------------*/

  const errorResponse = loginServiceResult.value.data

  if (loginServiceResult.value.status !== 422) {
    throw new UnhandledApiError({
      status: ApiFailure.UNPROCESSABLE_ERROR,
      message: `A non 422 httpStatus was returned on request: ${loginServiceEndpoint}`,
    })
  }

  const errorType = computeErrorType(loginServiceResult.value)

  const counter = errorResponse.error?.errorInfos?.counter

  return Err({
    type: errorType,
    counter: 4,
    errorResponse,
    ...(Array.isArray(counter) && { counter: Number(counter[0]) + 1 }),
  })
}

/* -------------------------------------------------------------------------------------------------
 * hooks
 * -----------------------------------------------------------------------------------------------*/

const useLoginMutation = () =>
  useSWRMutation(loginServiceEndpoint, loginFetcher, {
    populateCache: true,
  })
// const useLoginData = () =>
//   Ok<TloginResponse>({
//     status: "SUCCESS",
//     channel: 62,
//     requestId: "87eece31-9b6a-4c93-be79-99a9cabccb51",
//     datetime: "29-04-2022 09:34:53",
//     token:
//       "eyJjdHkiOiJKV1QiLCJlbmMiOiJBMjU2R0NNIiwidGFnIjoiZU81bGhxY2RJT3lsdnlPamtRcUVHdyIsImFsZyI6IkEyNTZHQ01LVyIsIml2IjoibU5Ec0kzQlpPeTdVVmd6aSJ9.a9NqEVYHoLZPBxAnLIzo9e3CkQAaJD2XWbaxPa7L5rY.MwG8tWgBj0WoJr3n.A9LhQxE5wOsKdiZYs193ya_h9kP5ig6rrD0vHanl2oAXIJqZcvItgstngd3n9sKjNO6y0YwFgAm6x1QeOdNfmQA_asnkOfOjofvn3K9ybmER0142OEPOHEUOLTDY4TuSqvY70tjFUl5jNpRdf-4d6sq2alxDSHAMBB9F-grvaPiYEbLYipXNPUAt-2QV4XNPq_G1o3WPDKNMhBzf0O5SRhb_KUkgkbzlYV2qav2J9oL83BbOdol6_tfBzGuSx3svaVC19aJhCNXA4ZY27wAEFVw4EZ6FHc78otIubLx2SIV2MJ3jn8gRLctZYiKyLJdAh_HKRJRUisz-tcKrnrUGO5ugUZc5msiXS02DnP2xOvmPiduBP-sPEuWgmGRlttthL9ddGrtFXQFBXzULd5XE5f3Zh3ST_qVspME0H0v00-QOjlp7aWcbm5qmtuLV0Vpu4JOvnMUhawz_Fe3aU5RIzruYjfQFuwWHeQmeENESdLyQ12y-E4kx_FhhZcBWgk4leka4ZGmJ51ZPlYsDai-Om7ci5JCDRw7O6xavWQ.017l892x1ntiVqlGw1_46A",
//     billCodeId: 836574,
//     passwordExpirationDays: 1,
//     isLoginBlocked: false,
//     isSecurityQuestionRequired: false,
//     isBillNotProtocolled: true,
//     flagAdvPlus: false,
//     url: "",
//     postLoginAction: { type: "ACCOUNT_BLOCKED" },
//     hasOther: false,
//     messages: [],
//     contactsToVerify: {
//       verificationEmailState: 2,
//       verificationPhoneNumberState: 0,
//       contacts: {
//         email: "braquicroxawu-2400@yopmail.com",
//         name: "ALDO",
//         surname: "BAGLIO",
//         phoneNumber: "3257866547",
//         prefix: "+39",
//         emailLockDays: 0,
//       },
//     },
//   })
// useMutationState<OkLoginResponse>(loginMutationKey).data.value

// const useLoginData = () =>
//   useMutationState<OkLoginResponse | undefined>(loginServiceEndpoint).data
//     ?.value
// ({
//   status: "SUCCESS",
//   channel: 62,
//   requestId: "db725530-394c-480c-84fa-e3d303b39313",
//   datetime: "15-03-2024 16:57:48",
//   passwordExpirationDays: 0,
//   isLoginBlocked: true,
//   loginBlockedMessage: "text.change.password.expired",
//   loginBlockedType: "EXPIRE",
//   isSecurityQuestionRequired: false,
//   isBillNotProtocolled: false,
//   flagAdvPlus: false,
//   emailAddress: "name@email.com",
//   prefix: "+39",
//   phoneNumber: "333123459",
//   fiscalCode: "CGNNTS00A11A003J",
// })

const usePostLoginRedirectUrl = () => {
  const previousLoginData = useLoginData()
  const { endCallbackUrl, flagConto, authType, channel } = useConfigParams()

  return (loginResponseData?: TloginResponse) => {
    const loginData = loginResponseData ?? previousLoginData

    if (!loginData || !endCallbackUrl) return ""

    return getRedirectUrl(
      loginData,
      endCallbackUrl,
      flagConto,
      authType,
      channel,
    )
  }
}

export {
  computeErrorType,
  computeHasOther,
  computePostLoginAction,
  loginResponseSchema,
  loginServiceEndpoint,
  useLoginMutation,
  usePostLoginRedirectUrl,
}
export type {
  BaseLoginPayload,
  ComputedLoginFields,
  ContactsToVerify,
  LoginErrorResponse,
  OkLoginResponse,
  TloginErrorResponse,
  TloginResponse,
}
